'use client';

import { useMemo, useState, type SetStateAction } from 'react';
import type { Product, ProductVariant, Money, Image as ShopifyImage } from '@/lib/shopify/types';
import { formatPrice } from '@/lib/shopify';
import {
  createDefaultCustomDesign,
  getProductPrintProfile,
  getSelectedProductPrintProfile,
  printProfilesEqual,
  type CustomDesignState,
  type CustomizerSide,
} from '@/lib/customizer';
import type { PendingArtworkFiles } from '@/lib/customizer-client';
import ProductGallery from '@/components/product/product-gallery';
import VariantSelector from '@/components/product/variant-selector';
import AddToCartButton from '@/components/product/add-to-cart-button';
import ProductAccordion from '@/components/product/product-accordion';
import RelatedProducts from '@/components/product/related-products';
import StickyAddToCart from '@/components/product/sticky-add-to-cart';
import WishlistButton from '@/components/product/wishlist-button';
import CustomSizeSelector from '@/components/product/custom-size-selector';
import ProductCustomizer from '@/components/product/product-customizer';
import ArtworkUpload from '@/components/product/artwork-upload';
import DigitisingFeeSelector from '@/components/product/digitising-fee-selector';
import SizeQuantitySelector, {
  type SizeQuantityLine,
  type SizeQuantityRow,
} from '@/components/product/size-quantity-selector';

export interface DigitisingFeeData {
  variantId: string;
  price: Money;
  productHandle: string;
}

// Shopify option naming is inconsistent across products — some use "Finish"
// (e.g. chefs-hat) and others use "Variant" (e.g. kids-baseball-caps) for the
// same concept (Embroidered Front / Back / Printed etc). Treat both as the
// finish option so the digitising-fee flow works on either.
const isFinishOptionName = (name: string) => {
  const n = name.toLowerCase();
  return n === 'finish' || n === 'variant';
};

function getInitialVariant(product: Product): ProductVariant | null {
  if (product.variants.length === 0) return null;

  const availableVariants = product.variants.filter((v) => v.availableForSale);
  if (availableVariants.length === 0) return product.variants[0];

  const getFinishValue = (variant: ProductVariant) =>
    variant.selectedOptions.find((opt) => isFinishOptionName(opt.name))?.value.toLowerCase();

  const printedFrontVariant = availableVariants.find((variant) => {
    const finishValue = getFinishValue(variant);
    if (!finishValue || finishValue.includes('embroider')) return false;
    const hasFront = finishValue.includes('front') || finishValue.includes('left');
    const hasBack = finishValue.includes('back') || finishValue.includes('right');
    return hasFront && !hasBack;
  });

  const printedFrontAndBackVariant = availableVariants.find((variant) => {
    const finishValue = getFinishValue(variant);
    if (!finishValue || finishValue.includes('embroider')) return false;
    return finishValue.includes('front') || finishValue.includes('left');
  });

  const nonEmbroideredVariant = availableVariants.find((variant) => {
    const finishValue = getFinishValue(variant);
    return finishValue && !finishValue.includes('embroider');
  });

  return printedFrontVariant || printedFrontAndBackVariant || nonEmbroideredVariant || availableVariants[0];
}

function getDigitisingSelection(finishValue: string, isEmbroidered: boolean) {
  if (!isEmbroidered) return { frontDigitising: false, backDigitising: false };

  const hasFront = finishValue.includes('front') || finishValue.includes('left');
  const hasBack = finishValue.includes('back') || finishValue.includes('right');

  if (hasFront && hasBack) return { frontDigitising: true, backDigitising: true };
  if (hasBack) return { frontDigitising: false, backDigitising: true };
  return { frontDigitising: true, backDigitising: false };
}

type SideAvailability = Record<CustomizerSide, boolean>;

function tokenizeImageText(value: string): string[] {
  return value.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
}

function getImageTokens(image: ShopifyImage): Set<string> {
  const filename = decodeURIComponent(image.url.split('/').pop()?.split('?')[0] || '');
  return new Set(tokenizeImageText(`${image.altText || ''} ${filename}`));
}

function imageMatchesSide(image: ShopifyImage, side: CustomizerSide): boolean {
  const tokens = getImageTokens(image);
  const sideTokens = side === 'back'
    ? ['back', 'rear', 'reverse', 'right']
    : ['front', 'left'];
  return sideTokens.some((token) => tokens.has(token));
}

function imageMatchesColor(image: ShopifyImage, colorValue?: string): boolean {
  if (!colorValue) return true;
  const colorTokens = tokenizeImageText(colorValue);
  if (colorTokens.length === 0) return true;
  const imageTokens = getImageTokens(image);
  return colorTokens.every((token) => imageTokens.has(token));
}

function imageMatchesExactColorSequence(image: ShopifyImage, colorValue?: string): boolean {
  if (!colorValue) return true;
  const colorSequence = tokenizeImageText(colorValue).join(' ');
  if (!colorSequence) return true;

  const filename = decodeURIComponent(image.url.split('/').pop()?.split('?')[0] || '');
  return [image.altText || '', filename]
    .map((value) => tokenizeImageText(value).join(' '))
    .some((value) => value.includes(colorSequence));
}

function uniqueImages(images: Array<ShopifyImage | undefined | null>): ShopifyImage[] {
  return [...new Map(
    images
      .filter((image): image is ShopifyImage => Boolean(image?.url))
      .map((image) => [image.url, image])
  ).values()];
}

function getSidePreviewImage(
  candidates: ShopifyImage[],
  colorValue: string | undefined,
  side: CustomizerSide,
  fallbackImage?: ShopifyImage
): ShopifyImage | undefined {
  // The selected variant is the strongest signal when its image matches the
  // requested colour and side. This also stops a generic one-word colour such
  // as "White" from matching an earlier Black/White gallery image.
  if (
    fallbackImage
    && imageMatchesSide(fallbackImage, side)
    && imageMatchesExactColorSequence(fallbackImage, colorValue)
  ) {
    return fallbackImage;
  }
  return getSideSpecificPreviewImage(candidates, colorValue, side) || fallbackImage;
}

function getSideSpecificPreviewImage(
  candidates: ShopifyImage[],
  colorValue: string | undefined,
  side: CustomizerSide
): ShopifyImage | undefined {
  const sideMatches = candidates.filter((image) => imageMatchesSide(image, side));
  if (sideMatches.length === 0) return undefined;
  if (colorValue) {
    // Match the complete colour phrase before falling back to an unordered
    // token match. Without this, White/French Navy also matches an earlier
    // French Navy/Classic Red/White image because both contain the same words.
    return sideMatches.find((image) => imageMatchesExactColorSequence(image, colorValue))
      || sideMatches.find((image) => imageMatchesColor(image, colorValue))
      || sideMatches[0];
  }
  return sideMatches[0];
}

function getFallbackSide(availability: SideAvailability): CustomizerSide {
  if (availability.front) return 'front';
  if (availability.back) return 'back';
  return 'front';
}

function getCustomizerSideAvailability(finishValue: string, supportsBack: boolean): SideAvailability {
  if (!supportsBack) return { front: true, back: false };

  const value = finishValue.toLowerCase();
  const hasFront = value.includes('front') || value.includes('left');
  const hasBack = value.includes('back') || value.includes('right');

  if (hasBack && !hasFront) return { front: false, back: true };
  if (hasFront && !hasBack) return { front: true, back: false };
  if (value && !hasFront && !hasBack) return { front: true, back: false };
  return { front: true, back: true };
}

function limitDesignToAvailableSides(
  design: CustomDesignState,
  availability: SideAvailability
): CustomDesignState {
  const blankSides = createDefaultCustomDesign(design.profile).sides;
  return {
    ...design,
    activeSide: availability[design.activeSide] ? design.activeSide : getFallbackSide(availability),
    sides: {
      front: availability.front ? design.sides.front : blankSides.front,
      back: availability.back ? design.sides.back : blankSides.back,
    },
  };
}

function applyPrintProfileToDesign(
  design: CustomDesignState,
  profile: CustomDesignState['profile']
): CustomDesignState {
  if (printProfilesEqual(design.profile, profile)) return design;
  return {
    ...design,
    activeSide: profile.supportsBack ? design.activeSide : 'front',
    profile,
  };
}

interface ProductPageClientProps {
  product: Product;
  relatedProducts: Product[];
  digitisingFees?: {
    front: DigitisingFeeData | null;
    back: DigitisingFeeData | null;
  };
  embedded?: boolean;
  hideRelated?: boolean;
  designExperience?: boolean;
  productOptionPreview?: boolean;
}

export default function ProductPageClient({ product, relatedProducts, digitisingFees, embedded = false, hideRelated = false, designExperience = false, productOptionPreview = false }: ProductPageClientProps) {
  const printProfile = getProductPrintProfile(product);
  const [selectedVariant, setSelectedVariant] = useState<ProductVariant | null>(() => getInitialVariant(product));
  const [customSize, setCustomSize] = useState('');
  const [sizeQuantities, setSizeQuantities] = useState<Record<string, number>>({});
  const [isCustomizerUploading, setIsCustomizerUploading] = useState(false);
  const [pendingArtworkFiles, setPendingArtworkFiles] = useState<PendingArtworkFiles>({});
  const [standaloneArtworkUrl, setStandaloneArtworkUrl] = useState('');
  const [standaloneArtworkFilename, setStandaloneArtworkFilename] = useState('');
  const [customDesign, setCustomDesign] = useState(() =>
    createDefaultCustomDesign(printProfile)
  );

  const handlePendingArtworkChange = (side: CustomizerSide, file: File | null) => {
    setPendingArtworkFiles((current) => ({ ...current, [side]: file }));
  };

  const handleDuplicatePendingArtwork = (fromSide: CustomizerSide, toSide: CustomizerSide) => {
    setPendingArtworkFiles((current) => ({
      ...current,
      [toSide]: current[fromSide] ?? null,
    }));
  };

  const handleClearPendingArtwork = () => setPendingArtworkFiles({});

  const handleStandaloneArtworkUpload = (url: string, filename: string) => {
    setStandaloneArtworkUrl(url);
    setStandaloneArtworkFilename(filename);
  };

  const handleStandaloneArtworkRemove = () => {
    setStandaloneArtworkUrl('');
    setStandaloneArtworkFilename('');
  };

  const price = selectedVariant?.price || product.priceRange.minVariantPrice;
  const compareAtPrice = selectedVariant?.compareAtPrice;
  const isOnSale = compareAtPrice && parseFloat(compareAtPrice.amount) > parseFloat(price.amount);
  const availableForSale = selectedVariant?.availableForSale ?? product.availableForSale;

  // Show custom size dropdown for clothing products that have no native Size option
  const CLOTHING_TYPES = ['t-shirt', 'hoodies', 'sweatshirts', 'jackets', 'workwear', 'trousers'];
  const isClothing = CLOTHING_TYPES.some(
    (type) => product.productType.toLowerCase().includes(type)
  );
  const needsCustomSize = isClothing && !product.options.some(
    (opt) => opt.name.toLowerCase() === 'size'
  ) && product.variants.length > 1;
  const nativeSizeOption = product.options.find((opt) => opt.name.toLowerCase() === 'size');
  const usesSizeQuantityGrid = !!nativeSizeOption && nativeSizeOption.values.length > 1;

  const sizeQuantityRows: SizeQuantityRow[] = nativeSizeOption
    ? nativeSizeOption.values.map((size) => {
        const variant = product.variants.find((candidate) =>
          candidate.selectedOptions.every((option) => {
            if (option.name === nativeSizeOption.name) return option.value === size;
            const selectedValue = selectedVariant?.selectedOptions.find((selected) =>
              selected.name === option.name
            )?.value;
            return selectedValue ? option.value === selectedValue : true;
          })
        ) || null;
        const available = Boolean(variant?.availableForSale);
        return {
          size,
          variant,
          available,
          quantity: available ? Math.max(0, sizeQuantities[size] || 0) : 0,
        };
      })
    : [];
  const sizeQuantityLines: SizeQuantityLine[] = sizeQuantityRows
    .filter((row): row is SizeQuantityRow & { variant: ProductVariant } =>
      row.available && !!row.variant && row.quantity > 0
    )
    .map((row) => ({
      variant: row.variant,
      quantity: row.quantity,
      size: row.size,
    }));
  const sizeQuantityTotal = sizeQuantityLines.reduce(
    (sum, line) => sum + parseFloat(line.variant.price.amount) * line.quantity,
    0
  );
  const stickyPrice: Money = usesSizeQuantityGrid && sizeQuantityTotal > 0
    ? { amount: sizeQuantityTotal.toFixed(2), currencyCode: price.currencyCode }
    : price;

  // Determine digitising fee based on selected Finish option
  const finishValue = selectedVariant?.selectedOptions.find(
    (opt) => isFinishOptionName(opt.name)
  )?.value || '';
  const normalizedFinishValue = finishValue.toLowerCase();
  const isEmbroidered = normalizedFinishValue.includes('embroider');
  const { frontDigitising, backDigitising } = getDigitisingSelection(normalizedFinishValue, isEmbroidered);
  const finishSideAvailability = getCustomizerSideAvailability(normalizedFinishValue, customDesign.profile.supportsBack);
  const previewFinishAttribute = productOptionPreview ? finishValue : undefined;

  const hasDigitisingFees = !!(digitisingFees?.front || digitisingFees?.back);

  // When a checkbox in DigitisingFeeSelector changes, switch the Finish variant
  // so the top Finish buttons stay in sync with the bottom digitising selector.
  // The reverse direction is derived from selectedVariant on every render.
  const handleDigitisingChange = (nextFront: boolean, nextBack: boolean) => {
    if (!selectedVariant) return;
    if (!nextFront && !nextBack) return; // disallow unchecking both — pick a Printed finish at the top for that

    const finishOption = product.options.find((o) => isFinishOptionName(o.name));
    if (!finishOption) return;

    const targetFinish = finishOption.values.find((val) => {
      const v = val.toLowerCase();
      if (!v.includes('embroider')) return false;
      const vHasFront = v.includes('front') || v.includes('left');
      const vHasBack = v.includes('back') || v.includes('right');
      const vIsBoth = vHasFront && vHasBack;
      if (nextFront && nextBack) return vIsBoth;
      if (nextFront) return vHasFront && !vHasBack;
      return !vHasFront && vHasBack;
    });
    if (!targetFinish) return;

    const newOptions: Record<string, string> = {};
    selectedVariant.selectedOptions.forEach((o) => { newOptions[o.name] = o.value; });
    newOptions[finishOption.name] = targetFinish;

    const nextVariant = product.variants.find((v) =>
      v.selectedOptions.every((opt) => newOptions[opt.name] === opt.value)
    );
    if (nextVariant) setSelectedVariant(nextVariant);
  };

  const handleFrontDigitisingChange = (checked: boolean) => {
    handleDigitisingChange(checked, backDigitising);
  };
  const handleBackDigitisingChange = (checked: boolean) => {
    handleDigitisingChange(frontDigitising, checked);
  };

  // Disable a checkbox only when toggling it would target a Finish variant that
  // doesn't exist (or is out of stock) for the current Colour/Size — mirrors the
  // greyed-out state on the top Finish selector. If the target is available,
  // the checkbox stays enabled; if the click would otherwise be invalid (e.g.
  // would leave no embroidery placement ticked), the bidirectional handler in
  // handleDigitisingChange silently no-ops and the checkbox reverts.
  const targetVariantAvailable = (nextFront: boolean, nextBack: boolean): boolean => {
    if (!selectedVariant) return false;
    if (!nextFront && !nextBack) return true; // not the disable's job to enforce this
    const finishOption = product.options.find((o) => isFinishOptionName(o.name));
    if (!finishOption) return false;
    const targetFinish = finishOption.values.find((val) => {
      const v = val.toLowerCase();
      if (!v.includes('embroider')) return false;
      const vHasFront = v.includes('front') || v.includes('left');
      const vHasBack = v.includes('back') || v.includes('right');
      const vIsBoth = vHasFront && vHasBack;
      if (nextFront && nextBack) return vIsBoth;
      if (nextFront) return vHasFront && !vHasBack;
      return !vHasFront && vHasBack;
    });
    if (!targetFinish) return false;
    const newOpts: Record<string, string> = {};
    selectedVariant.selectedOptions.forEach((o) => { newOpts[o.name] = o.value; });
    newOpts[finishOption.name] = targetFinish;
    return product.variants.some(
      (v) => v.availableForSale && v.selectedOptions.every((opt) => newOpts[opt.name] === opt.value)
    );
  };

  const frontDigitisingDisabled = !targetVariantAvailable(!frontDigitising, backDigitising);
  const backDigitisingDisabled = !targetVariantAvailable(frontDigitising, !backDigitising);

  // Image is driven by the selected Colour, not the full variant. Shopify variant
  // images are mis-assigned in two ways: (a) non-primary Finish variants often
  // point to a different colour's image (commonly Black), and (b) the first
  // variant for a colour is frequently the bad one. Pick the candidate whose
  // image filename actually matches the colour tokens; fall back to first.
  const selectedColorOpt = selectedVariant?.selectedOptions.find(
    (opt) => opt.name.toLowerCase() === 'color' || opt.name.toLowerCase() === 'colour'
  );

  const colorMatchedImage = useMemo(() => {
    if (!selectedColorOpt) return undefined;
    const candidates = product.variants.filter(
      (v) => v.image && v.selectedOptions.some(
        (opt) => opt.name === selectedColorOpt.name && opt.value === selectedColorOpt.value
      )
    );
    if (candidates.length === 0) return undefined;
    const tokens = selectedColorOpt.value.toLowerCase().split(/[^a-z0-9]+/).filter(Boolean);
    const matched = tokens.length
      ? candidates.find((v) => v.image && imageMatchesExactColorSequence(v.image, selectedColorOpt.value))
        || candidates.find((v) => {
          const url = (v.image?.url || '').toLowerCase();
          return tokens.every((t) => url.includes(t));
        })
      : undefined;
    return (matched || candidates[0]).image;
  }, [product.variants, selectedColorOpt]);

  const primaryImage = colorMatchedImage || selectedVariant?.image || product.featuredImage || product.images[0];
  const previewImageCandidates = useMemo(
    () => uniqueImages([
      ...product.images,
      ...product.variants.map((variant) => variant.image),
    ]),
    [product.images, product.variants]
  );
  const hasBackPreviewImage = useMemo(
    () => Boolean(getSideSpecificPreviewImage(previewImageCandidates, selectedColorOpt?.value, 'back')),
    [previewImageCandidates, selectedColorOpt?.value]
  );
  const previewSideAvailability: SideAvailability = {
    front: Boolean(primaryImage),
    back: customDesign.profile.supportsBack && hasBackPreviewImage,
  };
  const sideAvailability: SideAvailability = {
    front: finishSideAvailability.front && previewSideAvailability.front,
    back: finishSideAvailability.back && previewSideAvailability.back,
  };
  const sideAvailabilityNotes: Partial<Record<CustomizerSide, string>> = {
    back: finishSideAvailability.back && !previewSideAvailability.back
      ? 'Back design is unavailable until this product has a back/reverse image.'
      : undefined,
  };
  const previewCustomDesign = limitDesignToAvailableSides(customDesign, sideAvailability);
  const previewSide = previewCustomDesign.activeSide;
  const previewImage = getSidePreviewImage(
    previewImageCandidates,
    selectedColorOpt?.value,
    previewSide,
    primaryImage
  );
  const selectedPrintProfile = getSelectedProductPrintProfile(product, {
    variant: selectedVariant,
    image: previewImage,
    side: previewCustomDesign.activeSide,
  });
  const displayCustomDesign = applyPrintProfileToDesign(customDesign, selectedPrintProfile);
  const purchasableCustomDesign = limitDesignToAvailableSides(displayCustomDesign, sideAvailability);

  const handleCustomDesignChange = (value: SetStateAction<CustomDesignState>) => {
    setCustomDesign((current) => {
      const currentProfile = getSelectedProductPrintProfile(product, {
        variant: selectedVariant,
        image: previewImage,
        side: current.activeSide,
      });
      const currentWithProfile = applyPrintProfileToDesign(current, currentProfile);
      const next = typeof value === 'function' ? value(currentWithProfile) : value;
      const nextProfile = getSelectedProductPrintProfile(product, {
        variant: selectedVariant,
        image: previewImage,
        side: next.activeSide,
      });
      return applyPrintProfileToDesign(next, nextProfile);
    });
  };

  const images = previewImage
    ? [previewImage, ...product.images.filter((img) => img.url !== previewImage.url)]
    : product.images;

  if (designExperience) {
    return (
      <>
        <section className={embedded ? 'pt-0 pb-12 lg:pb-16' : 'container pt-12 pb-12 lg:pt-20 lg:pb-16'}>
          <div className="grid gap-6 xl:grid-cols-[minmax(0,1fr)_400px]">
            <div className="min-w-0 space-y-5">
              <div className="border border-[var(--color-border)] bg-white p-4 sm:p-5">
                <p className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                  Design online
                </p>
                <h1 className="mt-2 text-2xl font-bold text-[var(--color-text-primary)] lg:text-3xl">
                  {product.title}
                </h1>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <span className="text-xl font-semibold">{formatPrice(price)}</span>
                  {isOnSale && compareAtPrice && (
                    <>
                      <span className="text-base text-[var(--color-text-muted)] line-through">
                        {formatPrice(compareAtPrice)}
                      </span>
                      <span className="bg-black px-2 py-1 text-xs font-bold text-white">
                        SALE
                      </span>
                    </>
                  )}
                </div>
              </div>

              <ProductCustomizer
                product={product}
                design={displayCustomDesign}
                onChange={handleCustomDesignChange}
                sideAvailability={sideAvailability}
                sideAvailabilityNotes={sideAvailabilityNotes}
                previewImageUrl={previewImage?.url}
                previewImageAlt={previewImage?.altText || product.title}
                deferUpload
                pendingArtworkFiles={pendingArtworkFiles}
                onPendingArtworkChange={handlePendingArtworkChange}
                onDuplicatePendingArtwork={handleDuplicatePendingArtwork}
                onClearPendingArtwork={handleClearPendingArtwork}
                onUploadingChange={setIsCustomizerUploading}
              />
            </div>

            <aside className="space-y-5 xl:sticky xl:top-32 xl:self-start">
              <div className="border border-[var(--color-border)] bg-white p-4 sm:p-5">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <p className="text-xs font-semibold uppercase tracking-wider text-[var(--color-text-secondary)]">
                      Product setup
                    </p>
                    <p className="mt-1 text-sm font-semibold text-black">
                      {selectedVariant?.title || product.title}
                    </p>
                  </div>
                  <span className="shrink-0 text-lg font-semibold">{formatPrice(stickyPrice)}</span>
                </div>
              </div>

              {product.variants.length > 1 && (
                <VariantSelector
                  product={product}
                  selectedVariant={selectedVariant}
                  onVariantChange={setSelectedVariant}
                  hiddenOptionNames={usesSizeQuantityGrid && nativeSizeOption ? [nativeSizeOption.name] : undefined}
                />
              )}

              {usesSizeQuantityGrid && (
                <SizeQuantitySelector
                  rows={sizeQuantityRows}
                  currencyCode={price.currencyCode}
                  onQuantityChange={(size, quantity) => {
                    setSizeQuantities((current) => ({ ...current, [size]: quantity }));
                  }}
                  onClear={() => setSizeQuantities({})}
                />
              )}

              {isEmbroidered && hasDigitisingFees && (
                <DigitisingFeeSelector
                  frontSelected={frontDigitising}
                  backSelected={backDigitising}
                  onFrontChange={handleFrontDigitisingChange}
                  onBackChange={handleBackDigitisingChange}
                  frontPrice={digitisingFees!.front!.price}
                  backPrice={digitisingFees!.back!.price}
                  frontDisabled={frontDigitisingDisabled}
                  backDisabled={backDigitisingDisabled}
                />
              )}

              {needsCustomSize && (
                <CustomSizeSelector
                  selectedSize={customSize}
                  onSizeChange={setCustomSize}
                />
              )}

              <AddToCartButton
                variant={selectedVariant}
                availableForSale={availableForSale}
                customSize={needsCustomSize ? customSize : undefined}
                productId={product.id}
                productTitle={product.title}
                selectedImageUrl={previewImage?.url}
                selectedImageAlt={previewImage?.altText || product.title}
                customDesign={purchasableCustomDesign}
                isCustomizerUploading={isCustomizerUploading}
                pendingArtworkFiles={pendingArtworkFiles}
                sizeQuantityLines={sizeQuantityLines}
                requireSizeQuantities={usesSizeQuantityGrid}
                digitisingFees={digitisingFees}
                frontDigitising={frontDigitising}
                backDigitising={backDigitising}
                finishAttribute={previewFinishAttribute}
              />

              <WishlistButton product={product} variant="full" />

              <ProductAccordion descriptionHtml={product.descriptionHtml} product={product} />
            </aside>
          </div>
        </section>

        <StickyAddToCart
          variant={selectedVariant}
          price={stickyPrice}
          availableForSale={availableForSale}
          customSize={needsCustomSize ? customSize : undefined}
          productId={product.id}
          productTitle={product.title}
          selectedImageUrl={previewImage?.url}
          selectedImageAlt={previewImage?.altText || product.title}
          customDesign={purchasableCustomDesign}
          isCustomizerUploading={isCustomizerUploading}
          pendingArtworkFiles={pendingArtworkFiles}
          sizeQuantityLines={sizeQuantityLines}
          requireSizeQuantities={usesSizeQuantityGrid}
          digitisingFees={digitisingFees}
          frontDigitising={frontDigitising}
          backDigitising={backDigitising}
          finishAttribute={previewFinishAttribute}
        />
      </>
    );
  }

  return (
    <>
      {/* Main Product Section */}
      <section className={embedded ? 'pt-0 pb-12 lg:pb-16' : 'container pt-12 pb-12 lg:pt-20 lg:pb-16'}>
        <div className="grid lg:grid-cols-2 gap-8 lg:gap-16">
          {/* Left - Gallery */}
          <div>
            <ProductGallery
              key={previewImage?.url ?? 'no-image'}
              images={images}
              productTitle={product.title}
            />
          </div>

          {/* Right - Product Info */}
          <div className="lg:sticky lg:top-32 lg:self-start space-y-6">
            {/* Title */}
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-[var(--color-text-primary)]">
                {product.title}
              </h1>
              <p className="text-sm text-[var(--color-text-secondary)] mt-1">
                by <span className="font-bold text-[var(--color-text-primary)] tracking-wider">OKTSHIRT</span>
              </p>
            </div>

            {/* Price */}
            <div>
              <div className="flex items-center gap-3">
                <span className="text-xl lg:text-2xl font-semibold">
                  {formatPrice(price)}
                </span>
                {isOnSale && compareAtPrice && (
                  <>
                    <span className="text-lg text-[var(--color-text-muted)] line-through">
                      {formatPrice(compareAtPrice)}
                    </span>
                    <span className="bg-black text-white text-xs font-bold px-2 py-1">
                      SALE
                    </span>
                  </>
                )}
              </div>
            </div>

            {/* Variant Selector */}
            {product.variants.length > 1 && (
              <VariantSelector
                product={product}
                selectedVariant={selectedVariant}
                onVariantChange={setSelectedVariant}
                hiddenOptionNames={usesSizeQuantityGrid && nativeSizeOption ? [nativeSizeOption.name] : undefined}
              />
            )}

            {usesSizeQuantityGrid && (
              <SizeQuantitySelector
                rows={sizeQuantityRows}
                currencyCode={price.currencyCode}
                onQuantityChange={(size, quantity) => {
                  setSizeQuantities((current) => ({ ...current, [size]: quantity }));
                }}
                onClear={() => setSizeQuantities({})}
              />
            )}

            {/* Digitising Fee Selector (only for embroidered finishes) */}
            {isEmbroidered && hasDigitisingFees && (
              <DigitisingFeeSelector
                frontSelected={frontDigitising}
                backSelected={backDigitising}
                onFrontChange={handleFrontDigitisingChange}
                onBackChange={handleBackDigitisingChange}
                frontPrice={digitisingFees!.front!.price}
                backPrice={digitisingFees!.back!.price}
                frontDisabled={frontDigitisingDisabled}
                backDisabled={backDigitisingDisabled}
              />
            )}

            {/* Custom Size Selector (for products without native Size option) */}
            {needsCustomSize && (
              <CustomSizeSelector
                selectedSize={customSize}
                onSizeChange={setCustomSize}
              />
            )}

            <div id="product-artwork-upload" className="border border-[var(--color-border)] bg-white p-4 sm:p-5">
              <span className="mb-3 block text-sm font-medium uppercase tracking-wider">
                Upload Artwork
                <span className="ml-1 font-normal text-[var(--color-text-secondary)]">(optional)</span>
              </span>
              <ArtworkUpload
                artworkUrl={standaloneArtworkUrl}
                artworkFilename={standaloneArtworkFilename}
                guidanceHeading="Upload the Artwork NOT a Mock-up, Please"
                guidanceImageSrc="/images/artwork-vs-mockup.png"
                guidanceImageAlt="Upload the artwork print file, not a mock-up preview."
                onUpload={handleStandaloneArtworkUpload}
                onRemove={handleStandaloneArtworkRemove}
              />
            </div>

            {/* Add to Cart */}
            <AddToCartButton
              variant={selectedVariant}
              availableForSale={availableForSale}
              customSize={needsCustomSize ? customSize : undefined}
              productId={product.id}
              productTitle={product.title}
              selectedImageUrl={previewImage?.url}
              selectedImageAlt={previewImage?.altText || product.title}
              artworkUrl={standaloneArtworkUrl}
              isCustomizerUploading={isCustomizerUploading}
              sizeQuantityLines={sizeQuantityLines}
              requireSizeQuantities={usesSizeQuantityGrid}
              digitisingFees={digitisingFees}
              frontDigitising={frontDigitising}
              backDigitising={backDigitising}
              finishAttribute={previewFinishAttribute}
            />

            {/* Wishlist Button */}
            <WishlistButton product={product} variant="full" />

            {/* Accordion */}
            <ProductAccordion descriptionHtml={product.descriptionHtml} product={product} />
          </div>
        </div>
      </section>

      {/* Related Products */}
      {!hideRelated && relatedProducts.length > 0 && (
        <RelatedProducts products={relatedProducts} currentProductId={product.id} />
      )}

      {/* Sticky Add to Cart (Mobile) */}
      <StickyAddToCart
        variant={selectedVariant}
        price={stickyPrice}
        availableForSale={availableForSale}
        customSize={needsCustomSize ? customSize : undefined}
        productId={product.id}
        productTitle={product.title}
        selectedImageUrl={previewImage?.url}
        selectedImageAlt={previewImage?.altText || product.title}
        artworkUrl={standaloneArtworkUrl}
        isCustomizerUploading={isCustomizerUploading}
        sizeQuantityLines={sizeQuantityLines}
        requireSizeQuantities={usesSizeQuantityGrid}
        digitisingFees={digitisingFees}
        frontDigitising={frontDigitising}
        backDigitising={backDigitising}
        finishAttribute={previewFinishAttribute}
      />
    </>
  );
}
